import type { QueryClient } from '@tanstack/react-query';
import type { ConversationListItem, InboxCategory } from '../types/conversation-types';

/**
 * The conv-list-v2 query key prefix, centralized so every site that needs to read or write the
 * list cache uses the exact same string instead of re-typing the literal (drift between two
 * hand-typed copies of 'conv-list-v2' would silently fail to match a cached query).
 */
export const CONVERSATION_LIST_QUERY_KEY = 'conv-list-v2';

type ListCachePage = { pages: ConversationListItem[][]; pageParams: unknown[] };

/**
 * Patches ONE conversation row in place, across every cached `conv-list-v2` query for this
 * tenant (every filter combination the operator has open in a tab), without triggering a
 * refetch. This is item 6 of the call-centre inbox rework: the operator's own actions — sending a
 * message, marking read, resolving/reopening/archiving, changing AI mode, reassigning — used to
 * call `invalidateQueries`, which both refetches the whole list over the network AND (before the
 * stream-buffering in `useConversationStream.ts` existed) reordered the list at the exact moment
 * the operator was looking at the row they had just acted on.
 *
 * With the buffering in place, a bare `invalidateQueries` would no longer visibly reorder
 * anything while the operator is "busy" (their own action proves they are) — but it still fires an
 * unnecessary network round trip and leaves the row's own content stale until that round trip
 * resolves. Patching the row directly makes the operator's own action feel instant and costs zero
 * extra requests, which is the point of doing it here and not only relying on the freeze.
 *
 * Silently does nothing if the row is not present in a given cached page (e.g. a tab with a
 * filter that already excludes this conversation) — there is nothing to patch there, and the next
 * real poll will bring that page's own truth regardless.
 */
export function patchConversationInListCache(
  queryClient: QueryClient,
  tenantSchema: string,
  conversationId: string,
  patch: Partial<ConversationListItem>,
): void {
  queryClient.setQueriesData<ListCachePage>(
    { queryKey: [CONVERSATION_LIST_QUERY_KEY, tenantSchema] },
    (old) => {
      if (!old?.pages) return old;
      let changed = false;
      const pages = old.pages.map((page) =>
        page.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          changed = true;
          return { ...conversation, ...patch };
        }),
      );
      return changed ? { ...old, pages } : old;
    },
  );
}

// ============================================================================
// Patch builders — one pure function per action, each returning EXACTLY the fields that action
// changes on a conversation row. Kept separate from the call sites (useSendMessageV2.ts,
// useMarkAsRead.ts, CustomerHeader.tsx, ContextPanel.tsx, ConversationsV2.tsx) so
// conversation-list-cache-patch-completeness.test.ts can compare each one, field by field,
// against an independently-written "what the row should look like after this action" fixture
// BEFORE any call site was changed to use invalidateQueries → patch. See that test file and the
// progress log for why this ordering matters and what it caught.
// ============================================================================

/** After a message send succeeds (useSendMessageV2.onSuccess). Preview logic mirrors the RPC's
 *  own `LEFT(m.content, 100)` in get_tenant_conversations_v2 / get_tenant_conversations_v2_keyset. */
export function buildSendMessagePatch(message: {
  created_at: string;
  content: string | null;
  message_type: string;
}): Partial<ConversationListItem> {
  return {
    last_message_at: message.created_at,
    last_message_preview: (message.content ?? '').slice(0, 100),
    last_message_type: message.message_type,
  };
}

/** After mark-as-read succeeds (useMarkAsRead.onSuccess). */
export function buildMarkAsReadPatch(): Partial<ConversationListItem> {
  return { cached_unread_count: 0 };
}

/** After resolve / reopen / archive succeeds (CustomerHeader.tsx's setInboxCategory). */
export function buildInboxCategoryPatch(category: InboxCategory): Partial<ConversationListItem> {
  return { inbox_category: category };
}

/** After the AI-mode pill cycles (CustomerHeader.tsx's handleModeToggle). */
export function buildAiModePatch(
  mode: ConversationListItem['agent_mode'],
): Partial<ConversationListItem> {
  return { agent_mode: mode };
}

/** After reassignment succeeds (ContextPanel.tsx's handleAssign). Needs the email too —
 *  AssignmentDropdown's own user query already fetches it, so there is no reason to leave
 *  assigned_user_email stale until the next poll. */
export function buildAssignmentPatch(
  userId: string,
  userName: string,
  userEmail: string | null,
): Partial<ConversationListItem> {
  return {
    assigned_user_id: userId,
    assigned_user_name: userName,
    assigned_user_email: userEmail,
  };
}

/** After resolve-escalation succeeds (ConversationsV2.tsx's resolveEscalation). The edge
 *  function sets escalation_status = 'resolved' on success (grepped supabase/functions/
 *  resolve-escalation/index.ts) — this is that same value, applied to the cache instead of
 *  waiting for a poll. Not one of the five sites item 6 names explicitly, but patching it matters
 *  for item 4's escalated band: without it, a just-resolved conversation would keep rendering in
 *  the Escalated band (stale escalation_status) until the next poll. */
export function buildEscalationResolvedPatch(): Partial<ConversationListItem> {
  return { escalation_status: 'resolved' };
}
